import { cn } from "@/lib/utils";

/**
 * Wiederverwendbarer Dunkelblau/Gold-Baustein (backlog.md -> [Punkt 8]: "das aktuell nur
 * bei 'Top 10 der Woche' verwendete Dunkelblau/Gold-Layout auf weitere, aktuell
 * unauffällige Landingpage-Bereiche ausweiten ... idealerweise als ein gemeinsamer,
 * wiederverwendbarer Stil-Baustein statt mehrfacher Einzelumsetzung"). Nutzt die
 * bestehenden semantischen Farb-Tokens (`--primary`/`--gold`, siehe app/globals.css)
 * statt fixer Hex-Werte wie `Top10Export`/`ScoreCardExport` - dort bewusst hartkodiert,
 * weil eine EXPORTIERTE PNG-Grafik farbmodus-unabhängig aussehen muss, hier dagegen ein
 * normaler Seitenbereich, der denselben Tokens wie der Rest der App folgen soll.
 *
 * `pill`: kompakt, einzeilig, für platzknappe Bereiche (Startseiten-Hero über der Suche).
 * `block`: größere Karte mit Eyebrow-Label, für Zitat-/Hervorhebungs-Boxen (Preisbundle-
 * Slogan, künftig z.B. Quartalsbericht-CEO-Zitat).
 */
export function SignatureBanner({
  variant = "block",
  tone = "navy",
  eyebrow,
  className,
  children,
}: {
  variant?: "pill" | "block";
  /** "gold": invertierte Farbgebung (Gold-Flaeche, Navy-Schrift) fuer Stellen, die sich
   * gegen die uebrigen (navy-flaechigen) SignatureBanner absetzen sollen - Erik 2026-08-13,
   * konkret fuer den Preisbundle-Slogan gewuenscht. Default bleibt die urspruengliche
   * Navy-Flaeche/Gold-Akzent-Kombination, damit bestehende Verwendungsstellen
   * (Earnings-Zitat, Portfolio-News) unveraendert bleiben. */
  tone?: "navy" | "gold";
  eyebrow?: string;
  className?: string;
  children: React.ReactNode;
}) {
  if (variant === "pill") {
    return (
      <div
        className={cn(
          "inline-flex items-center gap-2 rounded-full border border-gold/40 bg-primary px-4 py-1.5 shadow-sm",
          className,
        )}
      >
        {eyebrow && (
          <span className="text-[0.65rem] font-semibold uppercase tracking-[0.1em] text-gold">{eyebrow}</span>
        )}
        <span className="text-xs font-medium text-primary-foreground">{children}</span>
      </div>
    );
  }

  const isGold = tone === "gold";

  return (
    <div
      className={cn(
        "rounded-2xl border px-6 py-5 shadow-sm",
        isGold ? "border-primary/15 bg-gold" : "border-gold/30 bg-primary",
        className,
      )}
    >
      {/* Kein eigenes text-align hier - erbt bewusst von der aufrufenden Stelle (z.B.
          `className="text-center"` fuer eine zentrierte Hero-Karte wie in pricing-bundle.tsx,
          sonst linksbuendig wie bei einer normalen Textsektion). */}
      {eyebrow && (
        <p
          className={cn(
            "text-xs font-semibold uppercase tracking-[0.15em]",
            isGold ? "text-ink/70" : "text-gold",
          )}
        >
          {eyebrow}
        </p>
      )}
      <div className={cn("mt-1.5", isGold ? "text-ink" : "text-primary-foreground")}>{children}</div>
    </div>
  );
}
